import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { skillService } from '../../services/skillService';
import { useNotificationStore } from '../../store/notificationStore';
import type { AgentSkillAssignmentDto, SkillDto } from '../../types/skill';
import { TextField } from '../../@core/components/mui';

interface AgentSkillListProps {
  agentId: string;
  readOnly?: boolean;
  providerId?: string;
}

/**
 * Skills attached to an agent (injected into system prompt on chat completion).
 */
export const AgentSkillList = ({ agentId, readOnly = false, providerId }: AgentSkillListProps) => {
  const { addNotification } = useNotificationStore();
  const [assignments, setAssignments] = useState<AgentSkillAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [skills, setSkills] = useState<SkillDto[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [versionPin, setVersionPin] = useState('');
  const [saving, setSaving] = useState(false);

  const loadAssignments = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await skillService.getAgentSkills(agentId);
      setAssignments(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load agent skills';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [agentId, addNotification]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const loadSkillsForPicker = useCallback(async () => {
    setSkillsLoading(true);
    try {
      const result = await skillService.getSkills({
        pageNumber: 1,
        pageSize: 100,
        searchTerm: search.trim() || undefined,
        isActive: true,
        providerId: providerId || undefined,
      });
      setSkills(result.items);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to load skills', 'error');
    } finally {
      setSkillsLoading(false);
    }
  }, [search, providerId, addNotification]);

  useEffect(() => {
    if (!pickerOpen) return;
    const delay = search.trim() ? 300 : 0;
    const t = setTimeout(() => {
      void loadSkillsForPicker();
    }, delay);
    return () => clearTimeout(t);
  }, [pickerOpen, search, loadSkillsForPicker]);

  const openPicker = () => {
    setSelectedSkillId(null);
    setVersionPin('');
    setSearch('');
    setPickerOpen(true);
  };

  const assignedIds = useMemo(() => new Set(assignments.map((a) => a.skillId)), [assignments]);

  const handleAssign = async () => {
    if (!selectedSkillId) {
      addNotification('Select a skill', 'warning');
      return;
    }
    if (assignedIds.has(selectedSkillId)) {
      addNotification('This skill is already assigned', 'warning');
      return;
    }
    const v = versionPin.trim();
    const versionNumber = v === '' ? null : parseInt(v, 10);
    if (v !== '' && Number.isNaN(versionNumber)) {
      addNotification('Version must be a number or empty for default', 'warning');
      return;
    }
    setSaving(true);
    try {
      await skillService.assignSkillToAgent(agentId, selectedSkillId, versionNumber);
      addNotification('Skill assigned to agent', 'success');
      setPickerOpen(false);
      loadAssignments();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to assign skill', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async (row: AgentSkillAssignmentDto) => {
    if (!window.confirm(`Remove skill "${row.skillName}" from this agent?`)) return;
    try {
      await skillService.unassignSkillFromAgent(agentId, row.id);
      addNotification('Skill removed', 'success');
      loadAssignments();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to remove skill', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Skills are merged into the agent&apos;s system prompt when users chat. No skill IDs are sent on each request.
        </Typography>
        {!readOnly && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openPicker}>
            Attach skill
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Skill</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Pinned version</TableCell>
              <TableCell align="right" width={72} />
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    No skills attached. Upload skills on the <strong>Skills</strong> page, then attach one here.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              assignments.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.skillName}</TableCell>
                  <TableCell>{a.skillSlug}</TableCell>
                  <TableCell>
                    {a.pinnedVersionNumber != null ? `v${a.pinnedVersionNumber}` : 'Default (floating)'}
                  </TableCell>
                  <TableCell align="right">
                    {!readOnly && (
                      <IconButton size="small" color="error" onClick={() => handleUnassign(a)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={pickerOpen} onClose={() => !saving && setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Attach skill to agent</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search skills…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {skillsLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
              {skills
                .filter((s) => !assignedIds.has(s.id))
                .map((s) => (
                  <ListItemButton
                    key={s.id}
                    selected={selectedSkillId === s.id}
                    onClick={() => setSelectedSkillId(s.id)}
                  >
                    <ListItemText primary={s.name} secondary={s.slug} />
                  </ListItemButton>
                ))}
            </List>
          )}
          <TextField
            fullWidth
            size="small"
            label="Pin to version (optional)"
            placeholder="Empty = use skill default version"
            value={versionPin}
            onChange={(e) => setVersionPin(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Enter a version number to pin, or leave empty."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAssign} disabled={saving || !selectedSkillId}>
            Attach
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
